import React, { useMemo } from "react";
import { Chip, FormControlLabel, Switch, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FiltersForm from "../FiltersForm";
import { formatWindowDate } from "../../pages/wqScorecard/buildScorecardTable";
import {
  CONTROL_LABELS,
  FLOW_OPTIONS,
  MAP_LINK_LIVE_ONLY,
  RESERVOIR_PERIOD_NOTE,
  STATISTIC_OPTIONS,
} from "../../pages/wqScorecard/scorecardCopy";

const useStyles = makeStyles((theme) => ({
  quickPicks: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  quickPickLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
    marginRight: theme.spacing(0.5),
  },
  // The reservoir switch sits on its own line under the selects, flush with
  // their left edge. FormControlLabel ships with a negative left margin to
  // hang the control into a form's gutter; there is no gutter here, so it is
  // zeroed and the switch's own edge adjustment lines the track up instead.
  toggleRow: {
    marginTop: theme.spacing(1.5),
  },
  toggle: {
    marginLeft: 0,
    marginRight: 0,
  },
  window: {
    marginTop: theme.spacing(2),
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  windowDates: {
    fontWeight: 600,
  },
}));

const formatWindow = (start, end) => {
  if (!start && !end) return null;
  const from = formatWindowDate(start);
  const to = formatWindowDate(end);
  return `${from ?? "?"} to ${to ?? "?"}`;
};

/**
 * Period, flow condition, statistic and which parameters to show.
 *
 * Built on the shared FiltersForm so these look and behave like the controls on
 * every other graph page: outlined, same widths, same wrapping. The group
 * quick-picks below are the one addition, because a sixteen-column table needs
 * a faster way to narrow than ticking boxes one at a time.
 */
export default function ScorecardPeriodControls({
  periods = [],
  period,
  flow,
  statistic,
  statisticLabel,
  allParameters = [],
  selectedParameters,
  isLive = false,
  includeReservoir = false,
  showReservoirToggle = false,
  onChange,
  disabled = false,
}) {
  const classes = useStyles();
  const selected = periods.find((p) => p.period_key === period);

  const allIds = useMemo(
    () => allParameters.map((p) => p.parameterid),
    [allParameters]
  );
  const effectiveParameters =
    selectedParameters && selectedParameters.length
      ? selectedParameters
      : allIds;

  const groups = useMemo(() => {
    const byGroup = [];
    allParameters.forEach((p) => {
      const existing = byGroup.find((g) => g.label === p.group);
      if (existing) existing.items.push(p);
      else byGroup.push({ label: p.group, items: [p] });
    });
    return byGroup;
  }, [allParameters]);

  const periodOptions = useMemo(
    () =>
      periods.map((option) => {
        const window = formatWindow(option.stats_start, option.stats_end);
        return {
          value: option.period_key,
          label: `${option.period_label}${window ? ` (${window})` : ""}`,
        };
      }),
    [periods]
  );

  // The URL can name a period before the list has loaded. Handing MUI a value
  // that matches none of its options logs an out-of-range warning, so fall back
  // to empty until the options exist; the key below then remounts the control
  // with the real value once they do.
  const periodValue = periodOptions.some((o) => o.value === period)
    ? period
    : "";

  const filters = useMemo(
    () => [
      {
        type: "select",
        param: "period",
        label: CONTROL_LABELS.period,
        width: 320,
        default: periodValue,
        options: periodOptions,
      },
      {
        type: "select",
        param: "flow",
        label: CONTROL_LABELS.flow,
        width: 200,
        default: flow,
        options: FLOW_OPTIONS,
      },
      {
        type: "select",
        param: "statistic",
        label: CONTROL_LABELS.statistic,
        width: 200,
        default: statistic,
        options: STATISTIC_OPTIONS.map((option) =>
          option.value === "pctile" &&
          statisticLabel &&
          statisticLabel !== "Median"
            ? { ...option, label: statisticLabel }
            : option
        ),
      },
      {
        type: "auto",
        multi: true,
        param: "params",
        label: CONTROL_LABELS.parameters,
        width: 340,
        default: effectiveParameters,
        options: allParameters.map((p) => ({
          value: p.parameterid,
          label: `${p.abbrev} — ${p.description}`,
        })),
      },
    ],
    [
      periodOptions,
      periodValue,
      flow,
      statistic,
      statisticLabel,
      allParameters,
      effectiveParameters,
    ]
  );

  // FilterSelect seeds its state once and never re-reads the prop, so a change
  // made anywhere but the control itself — the default period arriving, or the
  // switch to Live after a rebuild — would leave it showing a stale value.
  // Keying on what should be displayed remounts it when that happens.
  // Only the three FilterSelects hold stale state. FilterAuto derives from its
  // prop, so leaving it out keeps the parameter dropdown open while you tick
  // boxes instead of remounting after every click.
  //
  // periodOptions.length is in the key because the options arrive after the
  // first render: without it the period control would keep the empty value it
  // was forced to mount with and never show the selected period.
  const formKey = `${periodValue}|${flow}|${statistic}|${periodOptions.length}`;

  const isExactly = (ids) =>
    ids.length === effectiveParameters.length &&
    ids.every((id) => effectiveParameters.includes(id));

  return (
    <div>
      <FiltersForm key={formKey} config={filters} onChange={onChange} />

      {showReservoirToggle && (
        // Appends the reservoir rows and their regulatory columns to the
        // watershed card, so the table reads tributaries first, then the
        // lake they feed. A switch rather than a scorecard dropdown: the
        // comparison is the point.
        <div className={classes.toggleRow}>
          <FormControlLabel
            className={classes.toggle}
            control={
              <Switch
                size="small"
                color="primary"
                edge="start"
                checked={includeReservoir}
                disabled={disabled}
                onChange={(event) => {
                  const checked = event.target.checked;
                  onChange((prev) => ({ ...prev, reservoir: checked }));
                }}
                inputProps={{ "aria-label": CONTROL_LABELS.reservoir }}
              />
            }
            label={
              <Typography component="span" className={classes.quickPickLabel}>
                {CONTROL_LABELS.reservoir}
              </Typography>
            }
          />
        </div>
      )}

      {allParameters.length > 1 && (
        <div className={classes.quickPicks}>
          <Typography component="span" className={classes.quickPickLabel}>
            Show:
          </Typography>
          <Chip
            size="small"
            label="All parameters"
            clickable
            disabled={disabled}
            color={
              effectiveParameters.length === allIds.length
                ? "primary"
                : "default"
            }
            onClick={() => onChange((prev) => ({ ...prev, params: null }))}
          />
          {groups.map((group) => {
            const ids = group.items.map((p) => p.parameterid);
            return (
              <Chip
                key={group.label}
                size="small"
                label={group.label}
                clickable
                disabled={disabled}
                color={isExactly(ids) ? "primary" : "default"}
                onClick={() =>
                  onChange((prev) => ({
                    ...prev,
                    params: ids.length === allIds.length ? null : ids,
                  }))
                }
              />
            );
          })}
        </div>
      )}

      {selected && (
        <Typography className={classes.window}>
          {"Values cover "}
          <span className={classes.windowDates}>
            {formatWindow(selected.stats_start, selected.stats_end) ??
              "the selected period"}
          </span>
          {formatWindow(selected.trend_start, selected.trend_end) ? (
            <>
              {". Trends cover "}
              <span className={classes.windowDates}>
                {formatWindow(selected.trend_start, selected.trend_end)}
              </span>
              {"."}
            </>
          ) : (
            "."
          )}
          {includeReservoir && ` ${RESERVOIR_PERIOD_NOTE}`}
          {!isLive && ` ${MAP_LINK_LIVE_ONLY}`}
        </Typography>
      )}
    </div>
  );
}
